/**
 * API client.
 *
 * Nothing in this file can send an order (ARCHITECTURE §16). The dashboard
 * reads the same tables the trading loop writes, and the few writes it does
 * make — emergency stop, strategy toggle, model promote, event ack — are the
 * audited operator actions the API already gates by role.
 */

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api/v1';

const ACCESS_KEY = 'atlas.access_token';
const REFRESH_KEY = 'atlas.refresh_token';

export interface Tokens {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
}

export type Role = 'admin' | 'operator' | 'viewer';
export type TradingMode = 'backtest' | 'paper' | 'demo' | 'live';
export type Direction = 'long' | 'short';
export type ModelState =
  | 'training'
  | 'validated'
  | 'paper_test'
  | 'approved'
  | 'archived'
  | 'rejected';

export interface User {
  id: number;
  email: string;
  full_name: string | null;
  role: Role;
  is_active: boolean;
  last_login_at: string | null;
}

export interface ComponentHealth {
  status: string;
  detail: string | null;
  latency_ms: number | null;
}

export interface Health {
  status: string;
  environment: string;
  trading_mode: TradingMode;
  live_trading_enabled: boolean;
  version: string;
  server_time_utc: string;
  components: Record<string, ComponentHealth>;
}

export interface SystemStatus {
  environment: string;
  trading_mode: TradingMode;
  live_trading_enabled: boolean;
  version: string;
  server_time_utc: string;
  accounts_total: number;
  accounts_enabled: number;
  accounts_halted: number;
  strategies_enabled: number;
  open_positions: number;
  approved_models: number;
  unacknowledged_events: number;
  operator_events: number;
}

export interface Page<T> {
  items: T[];
  total: number;
  limit: number;
  offset: number;
}

export interface AccountSummary {
  id: number;
  name: string;
  broker: string | null;
  server: string | null;
  login: number | null;
  currency: string | null;
  trading_mode: TradingMode;
  magic_number: number | null;
  is_enabled: boolean;
  trading_halted: boolean;
  halt_reason: string | null;
}

export interface AccountDetail extends AccountSummary {
  leverage: number | null;
  gateway_url: string | null;
  peak_equity: string | null;
  trading_day_offset_min: number;
}

export type ConnectionState =
  | 'connected'
  | 'mismatch'
  | 'degraded'
  | 'down'
  | 'not_applicable';

export interface AccountSymbol {
  /** What the system calls it — what a job's `symbol` parameter wants. */
  symbol: string;
  /** What the terminal calls it. Usually identical; the mapping exists for
   *  the brokers where it is not. */
  broker_symbol: string;
  /** False = the broker offers it, but no mapping row exists yet, so a job
   *  naming it fails until one is created. */
  is_mapped: boolean;
}

export interface AccountSymbolList {
  items: AccountSymbol[];
  total: number;
  /** Why the broker's live list may be missing (gateway down, paper account). */
  gateway_detail: string | null;
}

export interface SeriesCoverage {
  symbol: string;
  timeframe: string;
  bars: number;
  first_bar_utc: string | null;
  last_bar_utc: string | null;
}

export interface SeriesCoverageList {
  items: SeriesCoverage[];
  total: number;
}

export interface SymbolMappingWrite {
  symbol: string;
  broker_symbol: string;
  is_enabled?: boolean;
}

export interface AccountConnection {
  account_id: number;
  state: ConnectionState;
  detail: string;
  checked_at_utc: string;
  gateway_status: string | null;
  terminal_connected: boolean | null;
  terminal_build: number | null;
  algo_trading_enabled: boolean | null;
  trade_allowed: boolean | null;
  last_tick_age_sec: number | null;
  market_open: boolean | null;
  server_time_utc: string | null;
  connected_login: number | null;
  expected_login: number | null;
  login_matches: boolean | null;
  broker_server: string | null;
  account_trade_mode: string | null;
  currency: string | null;
  leverage: number | null;
  balance: string | null;
  equity: string | null;
  margin: string | null;
  free_margin: string | null;
  margin_level: string | null;
  open_positions: number | null;
  guard_ok: boolean | null;
  guard_error: string | null;
}

export interface AccountInput {
  name: string;
  broker: string | null;
  server: string | null;
  login: number | null;
  currency: string | null;
  leverage: number | null;
  trading_mode: TradingMode;
  gateway_url: string | null;
  magic_number: number | null;
  trading_day_offset_min: number;
  is_enabled: boolean;
}

export interface EquityPoint {
  time_utc: string;
  equity: string;
  balance: string;
  open_positions: number;
}

export interface RiskLimitStatus {
  daily_realized_pnl: string;
  max_daily_loss_fraction: string;
  max_drawdown_fraction: string;
  peak_equity: string | null;
  open_positions: number;
  max_open_positions: number;
  trading_halted: boolean;
  halt_reason: string | null;
}

export interface DashboardResponse {
  account_id: number;
  account_name: string;
  trading_mode: string;
  login: number | null;
  balance: string | null;
  equity: string | null;
  currency: string | null;
  open_position_count: number;
  today_realized_pnl: string;
  risk: RiskLimitStatus;
  equity_curve: EquityPoint[];
  as_of_utc: string;
}

export interface PositionSummary {
  ticket: number;
  account_id: number;
  symbol: string;
  direction: Direction;
  volume: string;
  open_price: string;
  open_time_utc: string;
  stop_loss: string | null;
  take_profit: string | null;
  close_price: string | null;
  close_time_utc: string | null;
  net_profit: string | null;
  is_external: boolean;
  execution_id: string | null;
}

export interface PositionEvent {
  event_type: string;
  trigger: string;
  at_utc: string;
  detail: string | null;
  before_state: Record<string, unknown>;
  after_state: Record<string, unknown>;
}

export interface PositionDetail extends PositionSummary {
  magic_number: number;
  initial_stop_loss: string | null;
  events: PositionEvent[];
}

export interface ExecutionTrace {
  execution_id: string;
  state: string;
  requested_volume: string;
  filled_volume: string | null;
  fill_price: string | null;
  broker_ticket: number | null;
  retcode: number | null;
  retcode_class: string | null;
  resolution_attempts: number;
  transitions: Record<string, unknown>[];
  created_at_utc: string;
  completed_at_utc: string | null;
}

export interface RiskTrace {
  execution_id: string;
  status: string;
  decided_at_utc: string;
  volume: string | null;
  entry_price: string | null;
  stop_loss: string | null;
  take_profit: string | null;
  risk_amount: string | null;
  account_currency: string | null;
  conversion_rate: string | null;
  rejection_reason: string | null;
  checks: Record<string, unknown>[];
}

export interface SignalTrace {
  link: string;
  bar_open_time_utc: string;
  strategy_id: string;
  strategy_version: string;
  symbol: string;
  timeframe: string;
  direction: Direction | null;
  entry_type: string | null;
  regime: string | null;
  feature_set_version: string | null;
  reason: string;
  rationale: string | null;
}

export interface FeatureTrace {
  feature_set_version: string;
  computed_at: string;
  values: Record<string, number | null>;
}

export interface TraceResponse {
  position: PositionDetail;
  execution: ExecutionTrace | null;
  risk_decision: RiskTrace | null;
  signal: SignalTrace | null;
  features: FeatureTrace | null;
  notes: string[];
}

export interface RejectionCount {
  reason_code: string;
  count: number;
}

export interface FunnelStage {
  label: string;
  entering: number;
  dropped: number;
}

export interface FunnelResponse {
  from_utc: string;
  to_utc: string;
  symbol: string | null;
  candidates: number;
  rejected: number;
  selected: number;
  risk_approved: number;
  executed: number;
  by_reason: RejectionCount[];
  stages: FunnelStage[];
  reconciles: boolean;
}

export interface RejectionRow {
  bar_open_time_utc: string;
  strategy_id: string;
  symbol: string;
  timeframe: string;
  reason_code: string;
  detail: string;
  observed: Record<string, unknown>;
}

export interface ModelSummary {
  model_id: string;
  algorithm: string;
  state: ModelState;
  dataset_hash: string;
  feature_set_version: string;
  created_at_utc: string;
  state_changed_at_utc: string;
  state_changed_by: string | null;
}

export interface ModelDetail extends ModelSummary {
  seed: number;
  feature_names: string[];
  params: Record<string, unknown>;
  library_versions: Record<string, string>;
  metrics: Record<string, unknown>;
  metrics_at_approval: Record<string, unknown> | null;
  rejection_reasons: string[];
  mlflow_run_id: string | null;
}

export interface DatasetSummary {
  dataset_hash: string;
  feature_set_version: string;
  strategy_id: string;
  symbols: string[];
  timeframe: string;
  from_utc: string;
  to_utc: string;
  built_at_utc: string;
  row_count: number;
  positive_count: number;
  ambiguous_count: number;
  time_barrier_count: number;
  excluded_permanently_missing: number;
}

export interface DatasetDetail extends DatasetSummary {
  label_config: Record<string, unknown>;
  model_count: number;
}

export interface StrategySummary {
  id: number | null;
  strategy_id: string;
  symbol: string;
  timeframe: string;
  account_id: number | null;
  strategy_version: string;
  enabled: boolean;
  enabled_at: string | null;
  disabled_at: string | null;
}

export interface StrategyDetail extends StrategySummary {
  params: Record<string, unknown>;
  permitted_regimes: string[];
  last_changed_by: number | null;
  signal_count: number;
}

export interface AuditEntry {
  id: number;
  created_at: string;
  actor_user_id: number | null;
  actor_email: string | null;
  action: string;
  entity_type: string | null;
  entity_id: string | null;
  detail: string | null;
  correlation_id: string | null;
}

export interface SystemEvent {
  id: number;
  created_at: string;
  event_type: string;
  severity: string;
  service: string | null;
  account_id: number | null;
  symbol: string | null;
  message: string;
  metadata: Record<string, unknown> | null;
  requires_operator: boolean;
  acknowledged: boolean;
  acknowledged_at: string | null;
}

export interface ActionResult {
  ok: boolean;
  detail: string;
}

export interface ClosePositionResult {
  ok: boolean;
  ticket: number;
  requested_volume: string | null;
  filled_volume: string | null;
  retcode: number;
  retcode_class: string;
  detail: string;
}

export type JobStatus = 'queued' | 'running' | 'succeeded' | 'failed' | 'cancelled';

export interface JobField {
  name: string;
  label: string;
  /** `account` and `symbol` are int/string on the wire; they exist so the form
   *  can offer what is actually selectable instead of a free-text box. */
  type: 'string' | 'int' | 'float' | 'date' | 'bool' | 'select' | 'dataset' | 'account' | 'symbol'
    // Several stored symbols, comma-separated. Build dataset needs it: §13.4
    // rejects a model whose out-of-sample profit is >60% from one symbol.
    | 'symbols';
  required: boolean;
  default: unknown;
  choices: string[];
  help: string;
}

export interface JobSpec {
  kind: string;
  label: string;
  description: string;
  est_minutes: number;
  produces_artifact: string | null;
  danger: boolean;
  fields: JobField[];
}

export interface JobSummary {
  id: number;
  kind: string;
  status: JobStatus;
  exit_code: number | null;
  created_by_email: string | null;
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
  artifact_count: number;
  waiting_seconds: number | null;
  running_seconds: number | null;
}

export interface WorkerStatus {
  online: boolean;
  workers: string[];
  detail: string;
}

export interface JobDetail extends JobSummary {
  params: Record<string, unknown>;
  argv: string[];
  output: string;
  artifacts: string[];
  error: string | null;
}

export interface DatasetArtifact {
  job_id: number;
  path: string;
  label: string;
}

export interface BacktestSummary {
  id: number;
  strategy_id: string;
  strategy_version: string;
  symbol: string;
  timeframe: string;
  from_utc: string;
  to_utc: string;
  seed: number;
  config_hash: string;
  dataset_hash: string;
  initial_balance: string;
  final_balance: string;
  ambiguous_share: number;
  created_at: string;
}

export interface BacktestDetail extends BacktestSummary {
  metrics: Record<string, unknown>;
  breakdowns: Record<string, unknown>;
  flags: string[];
  trade_count: number;
}

export interface BacktestTrade {
  trade_index: number;
  symbol: string;
  direction: Direction;
  signal_bar_utc: string;
  entry_bar_utc: string;
  exit_bar_utc: string;
  entry_price: string;
  exit_price: string;
  stop_loss: string;
  take_profit: string | null;
  volume: string;
  net_profit: string;
  r_multiple: number;
  bars_held: number;
  exit_reason: string;
  entry_regime: string | null;
  ambiguous: boolean;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export const tokenStore = {
  get access() {
    return localStorage.getItem(ACCESS_KEY);
  },
  get refresh() {
    return localStorage.getItem(REFRESH_KEY);
  },
  save(tokens: Tokens) {
    localStorage.setItem(ACCESS_KEY, tokens.access_token);
    localStorage.setItem(REFRESH_KEY, tokens.refresh_token);
  },
  clear() {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
  },
};

type QueryValue = string | number | boolean | null | undefined;

function qs(params: Record<string, QueryValue>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== null && value !== undefined && value !== '') search.set(key, String(value));
  }
  const s = search.toString();
  return s ? `?${s}` : '';
}

async function parseError(response: Response): Promise<ApiError> {
  let code = 'http_error';
  let message = response.statusText || 'request failed';
  try {
    const body = await response.json();
    code = body.error ?? code;
    message = body.message ?? body.detail ?? message;
  } catch {
    // A non-JSON error body is still an error; the status carries the meaning.
  }
  return new ApiError(response.status, code, message);
}

async function request<T>(path: string, init: RequestInit = {}, retry = true): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body) headers.set('Content-Type', 'application/json');
  const access = tokenStore.access;
  if (access) headers.set('Authorization', `Bearer ${access}`);

  const response = await fetch(`${BASE_URL}${path}`, { ...init, headers });

  // One refresh attempt, then give up. Retrying a refresh that already failed
  // just turns an expired session into a loop.
  if (response.status === 401 && retry && tokenStore.refresh) {
    const refreshed = await refreshTokens();
    if (refreshed) return request<T>(path, init, false);
  }

  if (!response.ok) throw await parseError(response);
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

async function refreshTokens(): Promise<boolean> {
  const refresh_token = tokenStore.refresh;
  if (!refresh_token) return false;
  const response = await fetch(`${BASE_URL}/auth/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refresh_token }),
  });
  if (!response.ok) {
    tokenStore.clear();
    return false;
  }
  tokenStore.save((await response.json()) as Tokens);
  return true;
}

const post = <T>(path: string, body?: unknown): Promise<T> =>
  request<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) });

const patch = <T>(path: string, body: unknown): Promise<T> =>
  request<T>(path, { method: 'PATCH', body: JSON.stringify(body) });

const put = <T>(path: string, body: unknown): Promise<T> =>
  request<T>(path, { method: 'PUT', body: JSON.stringify(body) });

export interface PositionQuery {
  account_id: number;
  symbol?: string;
  open_only?: boolean;
  limit?: number;
  offset?: number;
}

export interface RejectionQuery {
  from_utc: string;
  to_utc: string;
  symbol?: string;
  reason_code?: string;
  limit?: number;
  offset?: number;
}

export interface ModelQuery {
  state?: ModelState;
  strategy_id?: string;
  limit?: number;
  offset?: number;
}

export interface AuditQuery {
  actor_user_id?: number;
  action?: string;
  entity_type?: string;
  since?: string;
  until?: string;
  limit?: number;
  offset?: number;
}

export interface EventQuery {
  severity?: string;
  acknowledged?: boolean;
  account_id?: number;
  requires_operator?: boolean;
  limit?: number;
  offset?: number;
}

export const api = {
  async login(email: string, password: string): Promise<Tokens> {
    const tokens = await request<Tokens>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    tokenStore.save(tokens);
    return tokens;
  },
  logout() {
    tokenStore.clear();
  },
  me: () => request<User>('/auth/me'),
  health: () => request<Health>('/health'),
  systemStatus: () => request<SystemStatus>('/system/status'),

  accounts: () => request<Page<AccountSummary>>('/accounts'),
  account: (id: number) => request<AccountDetail>(`/accounts/${id}`),
  createAccount: (body: AccountInput) => post<AccountDetail>('/accounts', body),
  updateAccount: (id: number, body: Partial<AccountInput>) =>
    patch<AccountDetail>(`/accounts/${id}`, body),
  accountConnection: (id: number) => request<AccountConnection>(`/accounts/${id}/connection`),
  deleteAccount: (id: number) =>
    request<ActionResult>(`/accounts/${id}`, { method: 'DELETE' }),
  /** Broker symbols + this account's mappings. Filtered server-side: the demo
   *  account reports 7391 symbols, which is not a list to ship to a browser. */
  accountSymbols: (id: number, q = '', limit = 200) =>
    request<AccountSymbolList>(`/accounts/${id}/symbols${qs({ q, limit })}`),
  mapAccountSymbol: (id: number, body: SymbolMappingWrite) =>
    put<AccountSymbol>(`/accounts/${id}/symbols`, body),
  /** What the candle store actually holds — the right question for every job
   *  that reads stored data rather than the broker. */
  coverage: () => request<SeriesCoverageList>('/market-data/coverage'),

  dashboard: (accountId: number, curvePoints = 200) =>
    request<DashboardResponse>(`/dashboard/${accountId}${qs({ curve_points: curvePoints })}`),

  positions: (q: PositionQuery) =>
    request<Page<PositionSummary>>(`/positions${qs({ ...q })}`),
  position: (ticket: number) => request<PositionDetail>(`/positions/${ticket}`),
  positionTrace: (ticket: number) => request<TraceResponse>(`/positions/${ticket}/trace`),
  closePosition: (ticket: number, accountId: number, reason: string, volume?: string) =>
    post<ClosePositionResult>(`/positions/${ticket}/close`, {
      account_id: accountId,
      reason,
      volume,
    }),

  funnel: (fromUtc: string, toUtc: string, symbol?: string) =>
    request<FunnelResponse>(`/signals/funnel${qs({ from_utc: fromUtc, to_utc: toUtc, symbol })}`),
  rejections: (q: RejectionQuery) =>
    request<Page<RejectionRow>>(`/signals/rejections${qs({ ...q })}`),

  models: (q: ModelQuery = {}) => request<Page<ModelSummary>>(`/models${qs({ ...q })}`),
  model: (id: string) => request<ModelDetail>(`/models/${id}`),
  promoteModel: (id: string, toState: ModelState, note?: string) =>
    post<ActionResult>(`/models/${id}/promote`, { to_state: toState, note }),

  datasets: (strategyId?: string) =>
    request<Page<DatasetSummary>>(`/datasets${qs({ strategy_id: strategyId })}`),
  dataset: (hash: string) => request<DatasetDetail>(`/datasets/${hash}`),

  strategies: (enabled?: boolean) =>
    request<Page<StrategySummary>>(`/strategies${qs({ enabled })}`),
  strategy: (id: number) => request<StrategyDetail>(`/strategies/${id}`),
  setStrategyEnabled: (id: number, enabled: boolean) =>
    post<ActionResult>(`/strategies/${id}/${enabled ? 'enable' : 'disable'}`),

  audit: (q: AuditQuery = {}) => request<Page<AuditEntry>>(`/audit${qs({ ...q })}`),

  events: (q: EventQuery = {}) => request<Page<SystemEvent>>(`/system/events${qs({ ...q })}`),
  ackEvent: (id: number) => post<ActionResult>(`/system/events/${id}/ack`),

  emergencyStop: (accountId: number, reason: string) =>
    post<ActionResult>('/system/emergency-stop', { account_id: accountId, reason }),
  clearHalt: (accountId: number, note?: string) =>
    post<ActionResult>('/system/emergency-stop/clear', { account_id: accountId, note }),

  jobCatalog: () => request<JobSpec[]>('/jobs/catalog'),
  workerStatus: () => request<WorkerStatus>('/jobs/worker'),
  jobDatasets: () => request<DatasetArtifact[]>('/jobs/datasets'),
  jobs: (q: { kind?: string; status?: JobStatus; limit?: number; offset?: number } = {}) =>
    request<Page<JobSummary>>(`/jobs${qs({ ...q })}`),
  job: (id: number) => request<JobDetail>(`/jobs/${id}`),
  createJob: (kind: string, params: Record<string, unknown>) =>
    post<JobSummary>('/jobs', { kind, params }),
  cancelJob: (id: number) => post<ActionResult>(`/jobs/${id}/cancel`),

  /** Fetch an artifact with the auth header and hand the browser a blob URL. */
  async downloadArtifact(jobId: number, name: string): Promise<void> {
    const headers = new Headers();
    const access = tokenStore.access;
    if (access) headers.set('Authorization', `Bearer ${access}`);
    const response = await fetch(`${BASE_URL}/jobs/${jobId}/artifacts/${encodeURIComponent(name)}`, {
      headers,
    });
    if (!response.ok) throw await parseError(response);
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `job-${jobId}-${name}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  },

  backtests: (q: { strategy_id?: string; symbol?: string; limit?: number; offset?: number } = {}) =>
    request<Page<BacktestSummary>>(`/backtests${qs({ ...q })}`),
  backtest: (id: number) => request<BacktestDetail>(`/backtests/${id}`),
  backtestTrades: (id: number, limit = 100, offset = 0) =>
    request<Page<BacktestTrade>>(`/backtests/${id}/trades${qs({ limit, offset })}`),
};
